import React, { useState, useEffect, useRef } from 'react';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { StyleSheet, View, StatusBar, Platform, ScrollView, Dimensions } from 'react-native';
import PlatformDetector from './utils/PlatformDetector';
import HeaderRojo from './components/Home/Header/HeaderRojo';
import Footer from './components/Home/Footer/Footer';
import Home from './components/Home/Body/Home';
import Productos from './components/Home/Body/Productos/Productos';
import DescripcionProducto from './components/Home/Body/Productos/DescripcionProducto';
import Pagina404 from './components/Pagina404';
import { UserProvider } from './context/UserContext';
import { ProductProvider } from './context/ProductContext';
import { CheckoutProvider } from './context/CheckoutContext';
import { CartProvider } from './context/CartContext';
import { FavoritosProvider } from './context/FavoritosContext';
import VerificationCodeModal from './components/Home/Header/AdminUsuario/VerificationCodeModal';
import PromocionDetalle from './components/Promociones/PromocionDetalle';
import PromosBancos from './components/PromosBancos/PromosBancos';
import GestionFinalizar from './components/Checkout/GestionFinalizar';
import PagoOK from './components/Checkout/FormasDePago/PagoOK';
import PagoFail from './components/Checkout/FormasDePago/PagoFail';
import PoliticasDeDevolucion from './components/Legales/PoliticasDeDevolucion';
import TerminosYCondiciones from './components/Legales/TerminosYCondiciones';
import Historia from './components/Legales/Historia';
import Manifiesto from './components/Legales/Manifiesto';
import ArrepentimientoDeCompra from './components/Legales/ArrepentimientoDeCompra';
import HeaderSuperior from './components/Home/Header/HeaderSuperior';
import { useFonts } from 'expo-font';
import {
  Montserrat_400Regular,
  Montserrat_500Medium,
  Montserrat_600SemiBold,
  Montserrat_700Bold,
  Montserrat_300Light,
  Montserrat_400Regular_Italic
} from '@expo-google-fonts/montserrat';
import LoadingScreen from './components/Common/LoadingScreen';
import { fuentes, colores } from './styles/variablesCss';
import { loadGoogleTagManager, trackPageView } from './utils/analytics';

const linking = {
  prefixes: ['http://localhost:8081'],
  config: {
    screens: {
      Home: '',
      Productos: 'productos',
      DescripcionProducto: 'producto/:id',
      PromosBancos: 'PromosBanco',
      Historia: 'Historia',
      Manifiesto: 'manifiesto',
      PoliticasDevolucion: 'politicas-de-devolucion',
      TerminosYCondiciones: 'terminos-y-condiciones',
      ArrepentimientoDeCompra: 'arrepentimiento-de-compra',
      validar: 'validar',
      CheckoutFinalizarCompra: 'checkout',
      PromocionDetalle: 'promocion/:id',
      Perfil: 'perfil',
      PagoOK: 'PagoOK',
      PagoFail: 'PagoFail',
      // Alias en minúsculas para pruebas locales (/pagoOK, /pagoFail)
      PagoOKWeb: 'pagoOK',
      PagoFailWeb: 'pagoFail',
      Pagina404: '*',
    },
  },
};

// Importar CSS para prevenir scroll horizontal en móvil web (solo en entorno web)
if (Platform.OS === 'web') {
  try {
    // @ts-ignore
    require('./styles/preventHorizontalScroll.css');
  } catch (e) {
    console.warn('No se pudo cargar el CSS para prevenir scroll horizontal');
  }
}

const Stack = createNativeStackNavigator();
const navigationRef = createNavigationContainerRef();

// Obtener la altura del StatusBar para dispositivos móviles
const getStatusBarHeight = () => {
  return StatusBar.currentHeight || 0;
};

// Calcular el margen superior para el Header
const getHeaderTopPadding = () => {
  return PlatformDetector.select({
    web: 0,
    android: getStatusBarHeight() > 0 ? getStatusBarHeight() : 24,
    ios: 44, // iOS generalmente necesita más espacio para el notch
    default: 0
  });
};

const AppContent = ({ children }) => {
  // Calculamos el padding superior para el Header
  const headerPadding = PlatformDetector.isMobile() ? getHeaderTopPadding() : 0;
  const getViewportBucket = (width) => {
    if (width < 576) return 'xs';
    if (width < 768) return 'sm';
    if (width < 992) return 'md';
    if (width < 1200) return 'lg';
    return 'xl';
  };
  const [viewportBucket, setViewportBucket] = useState(getViewportBucket(Dimensions.get('window').width));
  
  // Detectar si estamos en la página de checkout
  const [isCheckout, setIsCheckout] = useState(false);
  
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Verificar la URL actual
      const checkPath = () => {
        const isCheckoutPage = window.location.pathname.includes('/checkout');
        setIsCheckout(isCheckoutPage);
      };
      
      // Verificar al montar
      checkPath();
      
      // Escuchar cambios de navegación (back/forward)
      window.addEventListener('popstate', checkPath);
      
      // Interceptar pushState y replaceState para detectar navegación programática
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;
      
      window.history.pushState = function(...args) {
        originalPushState.apply(this, args);
        checkPath();
      };
      
      window.history.replaceState = function(...args) {
        originalReplaceState.apply(this, args);
        checkPath();
      };
      
      return () => {
        window.removeEventListener('popstate', checkPath);
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
      };
    }
  }, []);

  useEffect(() => {
    const handleViewportChange = ({ window }) => {
      const next = getViewportBucket(window.width);
      setViewportBucket((prev) => (prev === next ? prev : next));
    };

    const subscription = Dimensions.addEventListener('change', handleViewportChange);
    return () => {
      if (subscription?.remove) {
        subscription.remove();
      } else if (typeof Dimensions.removeEventListener === 'function') {
        Dimensions.removeEventListener('change', handleViewportChange);
      }
    };
  }, []);
  
  // Calcular la altura exacta de todo el header
  // En checkout: solo sección roja (60px)
  // En otras páginas: sección roja (60px) + sección blanca (50px) = 110px total
  const headerTotalHeight = isCheckout ? 60 : 135;
  
  return (
  <View style={styles.mainContainer}>
    <StatusBar backgroundColor="black" barStyle="light-content" translucent={true} />
    
    {/* Header fijo */}
    <View style={[styles.headerFixedContainer, { paddingTop: headerPadding }]}>
    <HeaderSuperior />
      <HeaderRojo />
    </View>
    {/* Contenedor principal para el contenido desplazable */}
    <ScrollView 
      style={[styles.mainContentWrapper, { paddingTop: headerTotalHeight }]}
      contentContainerStyle={styles.scrollContentContainer}
    >
      <View style={styles.pageContent} key={viewportBucket}>
        {children}
      </View>
      <Footer />
    </ScrollView>
  </View>
  );
};

const WrappedScreen = (Component) => (props) => (
  <AppContent>
    <Component {...props} />
  </AppContent>
);

export default function App() {
  // Cargar las fuentes Montserrat
  const [fontsLoaded] = useFonts({
    [fuentes.regular]: Montserrat_400Regular,
    [fuentes.bold]: Montserrat_700Bold,
    [fuentes.semiBold]: Montserrat_600SemiBold,
    [fuentes.medium]: Montserrat_500Medium,
    [fuentes.italic]: Montserrat_400Regular_Italic,
    [fuentes.light]: Montserrat_300Light,
  });

  const routeNameRef = useRef();

  useEffect(() => {
    loadGoogleTagManager();
  }, []);

  // Mostrar pantalla de carga mientras se cargan las fuentes
  if (!fontsLoaded) {
    return <LoadingScreen />;
  }

  return (
    <UserProvider>
      <ProductProvider>
        <FavoritosProvider>
          <CartProvider>
            <CheckoutProvider>
            <NavigationContainer
              linking={linking}
              ref={navigationRef}
              onReady={() => {
                const currentRoute = navigationRef.getCurrentRoute();
                routeNameRef.current = currentRoute?.name;
                trackPageView(currentRoute);
              }}
              onStateChange={() => {
                const currentRoute = navigationRef.getCurrentRoute();
                const currentRouteName = currentRoute?.name;

                if (routeNameRef.current !== currentRouteName) {
                  trackPageView(currentRoute);
                }

                routeNameRef.current = currentRouteName;
              }}
            >
        <Stack.Navigator initialRouteName="Home" screenOptions={{ headerShown: false }}>
          <Stack.Screen 
            name="Home" 
            component={WrappedScreen(Home)}
            options={{
              title: 'Supermercados Pingüino',
            }}
          />
          <Stack.Screen 
            name="Productos" 
            component={WrappedScreen(Productos)}
            options={{
              title: 'Productos',
            }}
          />
          <Stack.Screen 
            name="DescripcionProducto" 
            component={WrappedScreen(DescripcionProducto)}
            options={{
              title: 'Detalle del Producto',
            }}
          />
          <Stack.Screen 
            name="PromosBancos" 
            component={WrappedScreen(PromosBancos)}
            options={{
              title: 'Promos Bancos',
            }}
          />
          <Stack.Screen 
            name="validar" 
            component={WrappedScreen(VerificationCodeModal)}
            options={{
              title: 'validar',
            }}
          />
          <Stack.Screen 
            name="CheckoutFinalizarCompra" 
            component={WrappedScreen(GestionFinalizar)}
            options={{
              title: 'Checkout',
            }}
          />
           <Stack.Screen 
            name="PromocionDetalle" 
            component={WrappedScreen(PromocionDetalle)}
            options={{
              title: 'Detalle de Promoción',
             }}
          />
          <Stack.Screen 
            name="Perfil" 
            component={WrappedScreen(require('./components/Home/Header/AdminUsuario/Perfil').default)}
            options={{
              title: 'Mi Perfil',
            }}
          />
          <Stack.Screen 
            name="Historia" 
            component={WrappedScreen(Historia)}
            options={{
              title: 'Historia',
            }}
          />
          <Stack.Screen 
            name="Manifiesto" 
            component={WrappedScreen(Manifiesto)}
            options={{
              title: 'Manifiesto',
            }}
          />
          <Stack.Screen 
            name="PoliticasDevolucion" 
            component={WrappedScreen(PoliticasDeDevolucion)}
            options={{
              title: 'Políticas de Devolución',
            }}
          />
          <Stack.Screen 
            name="TerminosYCondiciones" 
            component={WrappedScreen(TerminosYCondiciones)}
            options={{
              title: 'Términos y Condiciones',
            }}
          />
          <Stack.Screen 
            name="ArrepentimientoDeCompra" 
            component={WrappedScreen(ArrepentimientoDeCompra)}
            options={{
              title: 'Arrepentimiento de compra',
            }}
          />
          <Stack.Screen 
            name="PagoOK" 
            component={WrappedScreen(PagoOK)}
            options={{
              title: 'Pago OK',
            }}
          />
          <Stack.Screen 
            name="PagoFail" 
            component={WrappedScreen(PagoFail)}
            options={{
              title: 'Pago Fallido',
            }}
          />
          {/* Alias web en minúsculas para rutas /pagoOK y /pagoFail */}
          <Stack.Screen 
            name="PagoOKWeb" 
            component={WrappedScreen(PagoOK)}
            options={{
              title: 'Pago OK',
            }}
          />
          <Stack.Screen 
            name="PagoFailWeb" 
            component={WrappedScreen(PagoFail)}
            options={{
              title: 'Pago Fallido',
            }}
          />
          <Stack.Screen 
            name="Pagina404" 
            component={WrappedScreen(Pagina404)}
            options={{
              title: 'Pagina 404',
            }}
          />
        </Stack.Navigator>
            </NavigationContainer>
            </CheckoutProvider>
          </CartProvider>
        </FavoritosProvider>
      </ProductProvider>
    </UserProvider>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: colores.fondo,
    // Aseguramos que la aplicación ocupe toda la pantalla
    paddingTop: 0,
    marginTop: 0,
  },
  bannerContainer: {
    position: 'absolute',
    top: 25,
    left: 0,
    right: 0,
    backgroundColor: colores.blanco,
    zIndex: 500, // z-index menor que el header y mucho menor que el carrito
  },
  headerFixedContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: colores.blanco,
    zIndex: 900, // Alto z-index pero menor que el carrito (1000)
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5, // Para Android
        }
    ),
  },
  mainContentWrapper: {
    flex: 1,
    width: '100%',
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  pageContent: {
    flex: 1,
    //minHeight: '100%',
  },

});